// frontend/src/components/Chat.js
import React, { useState, useEffect, useRef } from 'react';
import Message from './Message';
import Input from './Input';
import { getChat, sendMessage } from '../services/api';
import '../styles/Chat.css';

const Chat = ({ chatId }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const messagesContainerRef = useRef(null); // Nuovo ref per il container dei messaggi
  
  // Esempi di problemi matematici per la chat vuota
  const examplePrompts = [
    "Ciao Albert! Sto cercando di risolvere un problema ma non so da dove iniziare",
    "Puoi aiutarmi a sviluppare le mie abilità scientifiche?",
    "Ciao Albert! Sono bloccato su questo problema da mezz'ora, puoi aiutarmi?"
  ];
  
  // Carica messaggi iniziali
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const chat = await getChat(chatId);
        setMessages(chat.messages || []);
      } catch (error) {
        console.error('Errore durante il caricamento dei messaggi:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (chatId) {
      fetchMessages();
    }
  }, [chatId]);
  
  // Meccanismo di scrolling migliorato
  useEffect(() => {
    if (loading) return; // Non fare nulla se stiamo ancora caricando
    
    const scrollToBottom = () => {
      if (!messagesEndRef.current || !messagesContainerRef.current) return;
      
      try {
        // Usa setTimeout per assicurarsi che il rendering sia completo
        setTimeout(() => {
          // Calcola se siamo già vicini al fondo
          const container = messagesContainerRef.current;
          const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
          
          // L'ultimo messaggio è dell'utente o dell'assistente che sta digitando?
          const lastMessage = messages[messages.length - 1];
          const isUserLastSender = lastMessage?.role === 'user';
          const isTyping = messages.some(m => m.isTyping);
          
          // Scrolla solo se siamo già vicini al fondo o se è un nuovo messaggio dell'utente/typing
          if (isNearBottom || isUserLastSender || isTyping) {
            messagesEndRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'end' // Assicura che scrolliamo alla fine del contenuto
            });
          }
        }, 100);
      } catch (error) {
        console.error('Errore durante lo scrolling:', error);
        
        // Fallback: scrolla manualmente se necessario
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }
    };
    
    scrollToBottom();
  }, [messages, loading]);
  
  // Invia messaggio
  const handleSendMessage = async (content) => {
    if (!content.trim() || sending) return;
    
    try {
      setSending(true);
      
      // Optimistic UI update per messaggio utente
      const tempUserMsg = { 
        id: `temp-user-${Date.now()}`, 
        content, 
        role: 'user', 
        created_at: new Date().toISOString() 
      };
      setMessages(prev => [...prev, tempUserMsg]);
      
      // Aggiungi indicatore "sta scrivendo"
      const tempAssistantMsg = { 
        id: 'typing', 
        content: '', 
        role: 'assistant', 
        isTyping: true 
      };
      setMessages(prev => [...prev, tempAssistantMsg]);
      
      // Assicura lo scroll automatico dopo l'invio del messaggio
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
          });
        }
      }, 50);
      
      // Invia al server
      const newMessages = await sendMessage(chatId, { content });
      
      // Aggiorna con risposte reali
      setMessages(prev => {
        // Rimuovi messaggi temporanei
        const filtered = prev.filter(m => 
          m.id !== tempUserMsg.id && m.id !== 'typing'
        );
        // Aggiungi i nuovi messaggi reali
        return [...filtered, ...newMessages];
      });
      
    } catch (error) {
      console.error('Errore durante l\'invio del messaggio:', error);
      // Rimuovi indicatore di digitazione
      setMessages(prev => prev.filter(m => m.id !== 'typing'));
      // Mostra errore come messaggio di sistema
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        content: "Si è verificato un errore nell'invio del messaggio. Riprova.",
        role: 'system',
        created_at: new Date().toISOString()
      }]);
    } finally {
      setSending(false);
    }
  };
  
  // Seleziona un esempio predefinito
  const handleExampleClick = (example) => {
    handleSendMessage(example);
  };
  
  return (
    <div className="chat-container" ref={chatContainerRef}>
      <div className="messages-container" ref={messagesContainerRef}>
        {loading ? (
          <div className="loading-messages">
            <div className="loading-spinner"></div>
            <p>Caricamento conversazione...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-chat">
            <div className="welcome-duck">
              <img src="/images/duck.png" alt="Paperella" />
            </div>
            <h2>Studia con Albert!</h2>
            <div className="empty-chat-examples">
              <p>Prova con uno di questi esempi:</p>
              {examplePrompts.map((prompt, index) => (
                <div 
                  key={index} 
                  className="example-prompt" 
                  onClick={() => handleExampleClick(prompt)}
                >
                  {prompt}
                </div>
              ))}
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <Message 
              key={message.id} 
              message={message} 
              isTyping={message.isTyping}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="input-container-wrapper">
        <Input onSend={handleSendMessage} disabled={sending || loading} />
      </div>
    </div>
  );
};

export default Chat;